// inbox/CustomerCard.jsx — ملف العميل الموحّد الوحيد في كل المنصّة
// (CustomerCardModal). تُستهلك من صندوق الرسائل (client=محوّل inbox)
// وأيضاً من القائمة الموحّدة/دليل العملاء/صفحة عملاء رتم (بلا client →
// lib/api مباشرة) وبوابة CS (client.mode==="pin") — الأربعة على
// GET /api/customers/{key}/profile فقط. لا يوجد أي ملف عميل آخر في
// الكود (البند 1، جولة تصحيح 2026-10-14 المرحلة 1).
//
// جولة تصحيح 2026-10-14 (المرحلة 1) / البند 13 — إعادة ترتيب العرض فقط
// (بلا بيانات جديدة): رأس ثابت، 4 بطاقات مقاييس، خلاصة سطر واحد،
// accordions بالترتيب: الطلبات · الأصناف المفضّلة · القنوات · سجل
// التواصل الموحّد · العروض/الاستبيانات/التفاعلات. الألوان كلها من tokens
// ./theme (C.*) — لا hex جديد في هذا الملف (حارس:
// test_item13_customer_card_design_tokens_guard.py).
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "./Modal";
import { C } from "./theme";
import { PhoneText } from "./PhoneText";
import { INTERACTION_STATUS_LABELS } from "./enumLabels";
import { translateServerError } from "./serverErrorCodes";
import { api } from "../lib/api";
import { InvoiceModal } from "../POSInvoices";
import { InfoTip } from "../components/InfoTip";
import { zVar } from "../lib/zIndex";

// ─────────────────────────── تنسيق (عرض فقط، بلا منطق أعمال جديد) ───────────────────────────
const _ar = (iso) => (iso ? String(iso).replace("T", " ").slice(0, 16) : "—");
const _date = (iso) => (iso ? String(iso).slice(0, 10) : "—");
// ⑤/L3-ج — خانتان عشريتان كالمصدر الوحيد utils/format.js (MONEY).
const _money = (v) => (typeof v === "number" ? `${v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ﷼` : "—");
const _daysAgo = (iso) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return d >= 0 ? d : null;
};

// البند 9 — نفس منطق _every_n_days_ar في الباك إند حرفياً (صياغة عربية
// ثنائية: «كل يومين» لا «كل 2 يوماً»). عرض فقط، لا حساب جديد.
function everyNDaysAr(n) {
  const ni = Math.round(n);
  if (ni <= 1) return "كل يوم تقريباً";
  if (ni === 2) return "كل يومين تقريباً";
  return `كل ${ni} يوماً تقريباً`;
}

// البند 13/ج — «خلاصة العميل» سطر واحد، مبنيّة من حقول موجودة أصلاً
// (rhythm.avg_gap_days/is_late/days_since_last/no_recent_activity +
// top_items[0]) بقالب نصّي ثابت — بلا LLM وبلا أي بيانات جديدة.
function buildCustomerSummaryAr(rhythm, topItems) {
  if (!rhythm?.available) return rhythm?.reason || "تاريخ شراء غير كافٍ لحساب الإيقاع بعد";
  const parts = [];
  if (rhythm.avg_gap_days != null) parts.push(`يزور ${everyNDaysAr(rhythm.avg_gap_days)}`);
  if (rhythm.is_late && rhythm.days_since_last != null) {
    parts.push(`تأخر عن عادته · غائب ${Math.round(rhythm.days_since_last)} يوماً`);
  } else if (rhythm.no_recent_activity) {
    parts.push("لا نشاط حديث حالياً");
  }
  if (topItems?.[0]?.name) parts.push(`يفضّل ${topItems[0].name}`);
  return parts.length ? parts.join(" · ") : "تاريخ شراء غير كافٍ لحساب الإيقاع بعد";
}

// حالة الإرسال (البند 7) — ألوان عرض فقط حسب نص status_ar القادم من
// دالة واحدة في الباك إند (_derive_send_status_ar)؛ لا منطق تصنيف هنا.
function statusBadgeColors(statusAr) {
  if (!statusAr) return [C.neutralBg, C.neutralText];
  if (statusAr.includes("وصلت") || statusAr.includes("قُرئت")) return [C.successBg, C.successText];
  if (statusAr.includes("مقبول")) return [C.infoBgSoft, C.infoText];
  if (statusAr.includes("فشل") || statusAr.includes("مرفوض")) return [C.dangerBg, C.dangerText];
  return [C.neutralBg, C.neutralText];
}

const badge = (bg, color) => ({
  background: bg, color, borderRadius: 999, padding: "2px 9px",
  fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", lineHeight: 1.5, alignSelf: "center",
});

// ④ C4 — زر InfoTip هدف لمس 44×44 (يبقى كما هو)، لكنه كان يمدّ ارتفاع
// الشارة/سطر العنوان إلى 44px على iPad (شارة «زيارة/شهر» 48px مقابل 15-19).
// الحاوية بحجم الأيقونة فقط، والهدف اللمسي يفيض شفافاً بلا أثر في التخطيط.
const TipSlot = ({ text, testId }) => (
  <span data-testid={`${testId}-slot`} style={{ display: "inline-flex", width: 14, height: 14,
        alignItems: "center", justifyContent: "center", overflow: "visible", flexShrink: 0 }}>
    <InfoTip text={text} testId={testId} />
  </span>
);

const SectionError = ({ msg }) => (
  <div data-testid="inbox-card-section-error" style={{ fontSize: 11.5,
       color: C.dangerText, background: C.dangerBgSoft, borderRadius: 8,
       padding: "6px 9px" }}>
    تعذّر تحميل هذا القسم — باقي الملف يعمل بشكل طبيعي. ({msg || "خطأ"})
  </div>
);

const EmptyLine = ({ testId, text = "لا يوجد" }) => (
  <div data-testid={testId} style={{ fontSize: 11.5, color: C.textMuted, padding: "2px 2px 8px" }}>
    {text}
  </div>
);

// البند 13/د.5 — accordion قابل للطيّ، يُفتح افتراضياً فقط للطلبات
// والأصناف المفضّلة (defaultOpen)، الباقي مطويّ ابتداءً.
const Accordion = ({ title, testId, defaultOpen, count, children }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div data-testid={testId} style={{ borderTop: `1px solid ${C.borderSoft}` }}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testId}-toggle`}
        aria-expanded={open}
        style={{ width: "100%", display: "flex", justifyContent: "space-between",
                 alignItems: "center", background: "none", border: "none",
                 padding: "12px 2px", cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: C.textDark }}>
          {title}{count != null ? ` (${count})` : ""}
        </span>
        <span style={{ fontSize: 11, color: C.textMuted }}>{open ? "▲" : "▼"}</span>
      </button>
      {open && <div data-testid={`${testId}-body`} style={{ paddingBottom: 8 }}>{children}</div>}
    </div>
  );
};

// جدول بحاوية تمرير أفقي داخلي (البند 13/ح) — لا يتسبّب أبداً بتمرير
// أفقي للصفحة نفسها على الجوال/iPad، التمرير محصور داخل الجدول وحده.
const TableScroll = ({ children, testId }) => (
  <div data-testid={testId} style={{ overflowX: "auto", maxWidth: "100%" }}>
    {children}
  </div>
);

const TAG_BADGE = {
  refunded: [C.dangerBg, C.dangerText, "مرتجعة"],
  // البند 3 — نص صريح يوضّح أن الفاتورة المجانية لا تُحتسَب زيارة (لا
  // "مجانية" وحدها، التي قد تُقرأ كمجرّد ملاحظة سعر).
  free: [C.neutralBg, C.neutralText, "مجانية — لا تُحتسب زيارة"],
  paid: null,
};

// ─────────────────────────── أ) الرأس الثابت (Sticky) ───────────────────────────
const StickyHeader = ({ data, onContact }) => (
  <div data-testid="inbox-card-sticky-header"
    style={{ position: "sticky", top: -14, zIndex: zVar("extra_2"), background: C.white,
             margin: "-14px -18px 10px", padding: "14px 18px 10px",
             borderBottom: `1px solid ${C.borderSoft}` }}>
    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
      <strong data-testid="inbox-card-name" style={{ fontSize: 15 }}>
        {data.name_source === "phone"
          ? <PhoneText value={data.phone} testId="inbox-card-name-phone" />
          : data.name}
      </strong>
      {data.name_source === "whatsapp" && (
        <span style={badge(C.greenBgSoft, C.greenTextSoft)}>اسم واتساب</span>
      )}
      <span data-testid="inbox-card-segment"
        style={badge(data.segment ? C.warningBgSoft : C.neutralBg,
                     data.segment ? C.warningText : C.neutralText)}>
        {data.segment ? `${data.segment.emoji} ${data.segment.label_ar}` : "—"}
      </span>
      {data.is_team && (
        <span data-testid="inbox-card-team-badge" style={badge("#E0F2FE", "#075985")}>فريق</span>
      )}
      {data.unified && data.wa_optout && (
        <span data-testid="inbox-card-wa-optout" style={badge(C.dangerBg, C.dangerText)}>
          🚫 مستبعَد من واتساب
        </span>
      )}
    </div>
    <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
      {String(data.phone || "").startsWith("soc:") ? (
        <span data-testid="inbox-card-social-note">
          محادثة سوشل — {data.linked ? "مرتبطة بسجل مشتريات عبر رقم هاتف مربوط" : "غير مرتبطة برقم هاتف بعد"}
        </span>
      ) : (
        <>
          <PhoneText value={data.phone} testId="inbox-card-phone" />
          {data.most_visited_branch ? <> · 🏪 {data.most_visited_branch}</> : null}
        </>
      )}
    </div>
    {data.unified && !String(data.phone || "").startsWith("soc:") && (
      <div style={{ marginTop: 8 }}>
        <button type="button" data-testid="inbox-card-contact-direct"
          disabled={data.wa_optout}
          onClick={onContact}
          style={{
            border: "none", borderRadius: 10, padding: "7px 14px",
            fontSize: 12.5, fontWeight: 800, cursor: data.wa_optout ? "not-allowed" : "pointer",
            background: data.wa_optout ? C.neutralBg : C.accent,
            color: data.wa_optout ? C.textMuted : C.white,
          }}>
          💬 تواصل مباشر
        </button>
        <div data-testid="inbox-card-contact-note" style={{ fontSize: 10.5, color: C.textMuted, marginTop: 4 }}>
          {data.wa_optout
            ? "معطّل — هذا الرقم طلب الإيقاف عن واتساب."
            : data.can_free_text
              ? "نص حر متاح الآن (رد خلال آخر 24 ساعة)."
              : "قالب معتمَد فقط الآن (خارج نافذة الرد الحر)."}
        </div>
      </div>
    )}
  </div>
);

// ─────────────────────────── ب) صف المقاييس الأربعة ───────────────────────────
const MetricCard = ({ label, value, info, testId }) => (
  <div data-testid={testId} style={{ background: C.rowBg, border: `1px solid ${C.border}`,
       borderRadius: 12, padding: "10px 8px", textAlign: "center" }}>
    <div style={{ fontSize: 10.5, color: C.textMuted, display: "flex",
         alignItems: "center", justifyContent: "center", gap: 2 }}>
      <span>{label}</span>
      {info && <TipSlot text={info} testId={`${testId}-info`} />}
    </div>
    <div style={{ fontSize: 15, fontWeight: 800, color: C.textDark, marginTop: 4 }}>{value}</div>
  </div>
);

const MetricsRow = ({ data, isNarrow }) => {
  const daysAgo = _daysAgo(data.last_visit);
  return (
    <div data-testid="inbox-card-metrics-row"
      style={{ display: "grid", gridTemplateColumns: isNarrow ? "1fr 1fr" : "repeat(4, 1fr)",
               gap: 8, marginTop: 10 }}>
      <MetricCard testId="inbox-card-metric-visits" label="زيارات مدفوعة" value={data.visits ?? "—"}
        info="عدد الفواتير المدفوعة غير المسترجَعة وغير المجانية فقط." />
      {/* ⑤/L3-ج — الحقل غائب من الاستجابة (بلا صلاحية) ⇒ لا بطاقة إنفاق أصلاً. */}
      {data.total_spend !== undefined && (
        <MetricCard testId="inbox-card-metric-spend" label="إجمالي الإنفاق" value={_money(data.total_spend)}
          info="مجموع قيمة الفواتير المدفوعة طوال العمر." />
      )}
      <MetricCard testId="inbox-card-metric-avg" label="متوسط الفاتورة" value={_money(data.avg_invoice)}
        info="إجمالي الإنفاق ÷ عدد الزيارات المدفوعة." />
      <MetricCard testId="inbox-card-metric-last-visit" label="آخر زيارة"
        value={data.last_visit ? `${_date(data.last_visit)}${daysAgo != null ? ` (منذ ${daysAgo} يوماً)` : ""}` : "—"}
        info="تاريخ آخر فاتورة مدفوعة ومنذ كم يوماً من اليوم." />
    </div>
  );
};

// ─────────────────────────── ج) خلاصة العميل ───────────────────────────
const SummaryLine = ({ rhythm, topItems }) => {
  if (!rhythm) return null;
  return (
    <div data-testid="inbox-card-summary-line" style={{ marginTop: 12 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: C.textDark }}>
        {buildCustomerSummaryAr(rhythm, topItems)}
      </div>
      {rhythm.available && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
          {/* مراجعة ما بعد testing_agent — "لا نشاط حديث" يظهر في سطر
              الخلاصة أعلاه أصلاً (buildCustomerSummaryAr)؛ لا تكرار هنا. */}
          {!rhythm.no_recent_activity && rhythm.trend && (
            <span style={badge(C.successBg, C.successText)} data-testid="inbox-card-rhythm-trend-badge">
              الاتجاه: {rhythm.trend === "up" ? "تصاعدي" : rhythm.trend === "down" ? "تنازلي" : "مستقر"}
            </span>
          )}
          {rhythm.visits_per_month_lifetime && (
            <span style={{ ...badge(C.infoBg, C.infoText), display: "inline-flex", alignItems: "center", gap: 3 }}
              data-testid="inbox-card-visits-per-month-lifetime">
              {rhythm.visits_per_month_lifetime.value} زيارة/شهر طوال العمر
              <TipSlot text={rhythm.visits_per_month_lifetime.formula_ar} testId="inbox-card-visits-per-month-lifetime-info" />
            </span>
          )}
          {rhythm.avg_discount_pct != null && (
            <span style={badge(C.warningBgSoft, C.warningText)}>
              خصم متوسط {rhythm.avg_discount_pct}%
            </span>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────── د.1) الطلبات ───────────────────────────
const OrdersAccordion = ({ data, ordersMore, ordersHasMore, ordersLoadingMore, onLoadMore, onOpenInvoice }) => {
  const rows = [...(data.orders || []), ...ordersMore];
  return (
    <Accordion title="الطلبات" testId="inbox-card-orders" defaultOpen count={rows.length || null}>
      {data.no_purchase_history ? (
        <EmptyLine testId="inbox-card-no-purchases" text="لا توجد مشتريات مسجّلة بهذا الرقم." />
      ) : rows.length === 0 ? <EmptyLine testId="inbox-card-orders-empty" /> : (
        <TableScroll testId="inbox-card-orders-table-scroll">
          <table data-testid="inbox-card-orders-table" style={{ width: "100%", minWidth: 480, borderCollapse: "collapse", fontSize: 11.5 }}>
            <thead>
              <tr style={{ color: C.textMuted, textAlign: "right" }}>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}>التاريخ</th>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}>الفرع</th>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}>القناة</th>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}>الأصناف</th>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}>المبلغ</th>
                <th style={{ padding: "4px 6px", fontWeight: 700 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o, i) => {
                const itemsText = o.products?.length
                  ? `${o.products.slice(0, 2).map((p) => p.name).join("، ")}${o.products.length > 2 ? ` +${o.products.length - 2}` : ""}`
                  : "—";
                return (
                  <tr key={i} data-testid="inbox-card-order-row"
                    onClick={() => o.invoice_id && onOpenInvoice(o.invoice_id)}
                    style={{ borderTop: `1px solid ${C.borderSoft}`, cursor: o.invoice_id ? "pointer" : "default" }}>
                    <td style={{ padding: "6px", whiteSpace: "nowrap" }}>{_ar(o.at).slice(0, 16)}</td>
                    <td style={{ padding: "6px" }}>{o.branch || "—"}</td>
                    <td style={{ padding: "6px" }}>
                      <span style={badge(o.source === "taker" ? C.purpleBg : C.infoBgSoft,
                                          o.source === "taker" ? C.purpleText : C.infoText)}>
                        {o.channel_label || o.source_label}
                      </span>
                    </td>
                    <td style={{ padding: "6px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{itemsText}</td>
                    <td style={{ padding: "6px", fontWeight: 800, whiteSpace: "nowrap" }}>
                      {_money(o.amount)}
                      {TAG_BADGE[o.tag] && (
                        <div data-testid="inbox-card-order-tag" style={{ marginTop: 3 }}>
                          <span style={badge(TAG_BADGE[o.tag][0], TAG_BADGE[o.tag][1])}>{TAG_BADGE[o.tag][2]}</span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "6px" }}>
                      {o.invoice_id && (
                        <span data-testid="inbox-card-order-view" style={{ fontSize: 10.5, color: C.accent, fontWeight: 800 }}>
                          🧾 عرض
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
      )}
      {!data.no_purchase_history && ordersHasMore && (
        <button type="button" onClick={onLoadMore} disabled={ordersLoadingMore}
          data-testid="inbox-card-orders-load-more"
          style={{ width: "100%", marginTop: 8, padding: "7px 10px", borderRadius: 10,
                   border: `1px solid ${C.accent}`, background: C.white, color: C.accent,
                   fontSize: 12, fontWeight: 800, cursor: ordersLoadingMore ? "default" : "pointer",
                   opacity: ordersLoadingMore ? 0.6 : 1, fontFamily: "inherit" }}>
          {ordersLoadingMore ? "⏳ جارٍ التحميل…" : "تحميل المزيد"}
        </button>
      )}
      {data.taker_unlinked_note && (
        <div data-testid="inbox-card-taker-note" style={{ fontSize: 10.5, color: C.textMuted, marginTop: 6, fontStyle: "italic" }}>
          {data.taker_unlinked_note}
        </div>
      )}
    </Accordion>
  );
};

// ─────────────────────────── د.2) الأصناف المفضّلة ───────────────────────────
const FavoritesAccordion = ({ topItems }) => (
  <Accordion title="الأصناف المفضّلة" testId="inbox-card-favorites" defaultOpen count={topItems?.length || null}>
    {!topItems?.length ? <EmptyLine testId="inbox-card-favorites-empty" /> : (
      <TableScroll testId="inbox-card-favorites-table-scroll">
        <table data-testid="inbox-card-favorites-table" style={{ width: "100%", minWidth: 280, borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ color: C.textMuted, textAlign: "right" }}>
              <th style={{ padding: "4px 6px", fontWeight: 700 }}>الصنف</th>
              <th style={{ padding: "4px 6px", fontWeight: 700 }}>العدد</th>
              <th style={{ padding: "4px 6px", fontWeight: 700 }}>المبلغ</th>
            </tr>
          </thead>
          <tbody>
            {topItems.map((t, i) => (
              <tr key={i} data-testid="inbox-card-favorite-row" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
                <td style={{ padding: "6px" }}>{t.name}</td>
                <td style={{ padding: "6px" }}>{t.qty}</td>
                <td style={{ padding: "6px", fontWeight: 700 }}>{_money(t.revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    )}
  </Accordion>
);

// ─────────────────────────── د.3) القنوات ───────────────────────────
const ChannelMiniCard = ({ label, count, revenue, testId, bg, color }) => (
  <div data-testid={testId} style={{ background: bg, borderRadius: 10, padding: "8px 6px", textAlign: "center", flex: 1 }}>
    <div style={{ fontSize: 10.5, color, fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 800, color: C.textDark, marginTop: 2 }}>{count}</div>
    <div style={{ fontSize: 10, color: C.textMuted }}>{_money(revenue)}</div>
  </div>
);

const ChannelsAccordion = ({ channels }) => (
  <Accordion title="القنوات" testId="inbox-card-channels" count={null}>
    {!channels ? <EmptyLine testId="inbox-card-channels-empty" /> : (
      <>
        <div style={{ display: "flex", gap: 8 }}>
          <ChannelMiniCard testId="inbox-card-channel-direct" label="🏪 مباشر"
            count={channels.direct?.visits ?? 0} revenue={channels.direct?.revenue}
            bg={C.infoBgSoft} color={C.infoText} />
          <ChannelMiniCard testId="inbox-card-channel-taker-pickup" label="🛍️ استلام تكر"
            count={channels.taker_pickup?.orders ?? 0} revenue={channels.taker_pickup?.revenue}
            bg={C.successBg} color={C.successText} />
          <ChannelMiniCard testId="inbox-card-channel-taker-delivery" label="🛵 توصيل تكر"
            count={channels.taker_delivery?.orders ?? 0} revenue={channels.taker_delivery?.revenue}
            bg={C.purpleBg} color={C.purpleText} />
        </div>
        {channels.taker_matched && (
          <div style={{ marginTop: 6 }}>
            <span data-testid="customer-taker-matched-badge" style={badge(C.purpleBg, C.purpleText)}>مطابَق في تكر</span>
          </div>
        )}
        <div style={{ fontSize: 10, color: C.textMuted, marginTop: 6 }}>
          تكر يستبعد فرع الاختبار وشباك المجانية.
        </div>
      </>
    )}
  </Accordion>
);

// ─────────────────────────── د.4) سجل التواصل (موحّد) ───────────────────────────
const CommsTimelineAccordion = ({ timeline, sectionError }) => (
  <Accordion title="سجل التواصل" testId="inbox-card-comms-timeline" count={timeline?.total_count ?? null}>
    {sectionError ? <SectionError msg={sectionError} /> : !timeline?.items?.length ? (
      <EmptyLine testId="inbox-card-comms-timeline-empty" />
    ) : (
      timeline.items.slice(0, 20).map((m, i) => {
        const [bg, color] = statusBadgeColors(m.status_ar);
        return (
          <div key={i} data-testid="inbox-card-comm-row"
            style={{ padding: "7px 9px", borderRadius: 8, background: C.rowBg, marginBottom: 5, fontSize: 11.5 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 4 }}>
              <strong>{_ar(m.at)}</strong>
              <span style={badge(bg, color)} data-testid="inbox-card-comm-status">{m.status_ar}</span>
            </div>
            <div style={{ color: C.textMuted, marginTop: 3 }}>
              {m.kind === "template" ? "قالب نظام" : "نص"}: {m.content || "—"}
              {m.campaign_name ? ` · ${m.campaign_name}` : ""}
            </div>
            {m.purchased_after !== null && m.purchased_after !== undefined && (
              <div style={{ marginTop: 3 }}>
                <span style={badge(m.purchased_after ? C.infoBgSoft : C.neutralBg,
                                    m.purchased_after ? C.infoText : C.neutralText)}>
                  {m.purchased_after ? `اشترى خلال ${timeline.attribution_window_days} يوم` : "لم يشترِ بعدها"}
                </span>
              </div>
            )}
          </div>
        );
      })
    )}
  </Accordion>
);

// ─────────────────────────── د.5) العروض · الاستبيانات · التفاعلات ───────────────────────────
const OffersSurveysInteractionsAccordion = ({ data, onOpenInvoice }) => {
  const offers = data.offers?.items || [];
  const surveys = data.surveys?.items || [];
  const interactions = data.interactions || [];
  const allEmpty = !offers.length && !surveys.length && !interactions.length
    && !data.sectionErrors.offers && !data.sectionErrors.surveys && !data.sectionErrors.interactions;
  const totalCount = offers.length + surveys.length + interactions.length;
  return (
    <Accordion title="العروض · الاستبيانات · التفاعلات" testId="inbox-card-offers-surveys-interactions"
      count={totalCount || null}>
      {allEmpty ? <EmptyLine testId="inbox-card-offers-surveys-interactions-empty" /> : (
        <>
          {data.sectionErrors.offers ? <SectionError msg={data.sectionErrors.offers} /> : offers.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.textMuted, marginBottom: 4 }}>العروض</div>
              {offers.map((of, i) => (
                <div key={i} data-testid="inbox-card-offer-row"
                  style={{ padding: "6px 9px", borderRadius: 8, background: C.rowBg, marginBottom: 5, fontSize: 11.5 }}>
                  <strong>{of.label}</strong> ({of.code}) · استُخدم {of.invoices} مرة · آخر استخدام: {_date(of.last_used)}
                </div>
              ))}
            </div>
          )}
          {data.sectionErrors.surveys ? <SectionError msg={data.sectionErrors.surveys} /> : surveys.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.textMuted, marginBottom: 4 }}>الاستبيانات</div>
              {surveys.map((sv, i) => (
                <div key={i} data-testid="inbox-card-survey-row"
                  style={{ padding: "6px 9px", borderRadius: 8, background: C.rowBg, marginBottom: 5, fontSize: 11.5 }}>
                  <strong>{sv.survey_title}</strong> · دُعي: {_date(sv.invited_at)}{" · "}
                  <span style={badge(sv.responded ? C.successBg : C.warningBgSoft,
                                      sv.responded ? C.successText : C.warningText)}>
                    {sv.responded ? "أجاب" : "لم يُجب"}
                  </span>
                </div>
              ))}
            </div>
          )}
          {data.sectionErrors.interactions ? <SectionError msg={data.sectionErrors.interactions} /> : interactions.length > 0 && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.textMuted, marginBottom: 4 }}>التفاعلات السابقة</div>
              {interactions.map((it, i) => (
                <div key={i} data-testid="inbox-card-interaction-row"
                  style={{ padding: "7px 10px", borderRadius: 10, background: C.rowBg, marginBottom: 6, fontSize: 12 }}>
                  <div>
                    <strong>{_date(it.opened_at)}</strong>{" · "}{it.type_label}
                    {it.products?.length > 0 && ` · ${it.products.join("، ")}`}
                  </div>
                  <div style={{ color: C.textMuted, marginTop: 2 }}>
                    الحالة: {INTERACTION_STATUS_LABELS[it.status] || it.status}
                    {it.resolution_label ? ` · الحل: ${it.resolution_label}` : ""}
                  </div>
                  {it.compensation && (
                    <div data-testid="inbox-card-compensation" style={{ marginTop: 3 }}>
                      <span style={badge(it.compensation.value_known ? C.infoBgSoft : C.neutralBg,
                                          it.compensation.value_known ? C.infoText : C.neutralText)}>
                        {it.compensation.value_known
                          ? `🎁 تعويض: ${it.compensation.type === "amount" ? "مبلغ" : it.compensation.type === "product" ? "منتج" : "كود"} — ${it.compensation.value}`
                          : it.compensation.label}
                      </span>
                    </div>
                  )}
                  {it.invoice_id && (
                    <div style={{ marginTop: 3 }}>
                      <button type="button" data-testid="inbox-card-interaction-invoice-link"
                        onClick={() => onOpenInvoice(it.invoice_id)}
                        style={{ background: "none", border: "none", padding: 0, color: C.accent,
                                 fontSize: 11, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
                        🧾 الفاتورة المرتبطة: #{it.invoice_id}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Accordion>
  );
};

// B2 — يغلق POSPage (إن كان مفتوحاً) ثم ينتقل إلى صندوق الرسائل مع فتح
// محادثة هذا الرقم تحديداً (نفس آلية hash `conv/<phone>` المستخدَمة أصلاً
// في UnifiedInbox.jsx، ونفس نمط ?tab=comms_hub&sub=unified المعتمَد في
// WhatsAppPanel.jsx — لا تعديل هناك، إعادة استخدام فقط).
function openInboxForPhone(phone) {
  try {
    const digits = String(phone || "").replace(/[^\d]/g, "");
    const u = new URL(window.location.href);
    u.searchParams.set("tab", "comms_hub");
    u.searchParams.set("sub", "unified");
    window.history.replaceState(null, "", u.toString());
    if (digits) window.location.hash = `conv/${digits}`;
    window.dispatchEvent(new CustomEvent("tw-navigate", { detail: { tab: "comms_hub" } }));
  } catch (_e) { /* noop */ }
}

/** يحوّل استجابة /customers/{key}/profile إلى بنية واحدة يستهلكها الرسم. */
function normalizeCardData(raw) {
  const ip = raw.sections?.identity_purchase || {};
  return {
    name: ip.name, name_source: ip.name_source, phone: raw.customer_phone_key,
    linked: ip.linked, link_status: ip.link_status,
    segment: ip.segment, visits: ip.visits, last_visit: ip.last_visit,
    last_branch: ip.most_visited_branch, total_spend: ip.total_spend,
    avg_invoice: ip.avg_invoice, first_visit: ip.first_visit,
    most_visited_branch: ip.most_visited_branch, top_items: ip.top_items || [],
    orders: ip.orders || [], no_purchase_history: ip.no_purchase_history,
    orders_has_more: !!ip.orders_has_more,
    taker_unlinked_note: ip.taker_unlinked_note,
    channels: ip.channels,
    interactions: (raw.sections?.interactions?.items) || [],
    wa_optout: !!raw.wa_optout, is_team: !!raw.is_team, can_free_text: !!raw.can_free_text,
    comms_timeline: raw.sections?.comms_timeline,
    offers: raw.sections?.offers,
    surveys: raw.sections?.surveys,
    rhythm: raw.sections?.rhythm,
    sectionErrors: raw.errors || {}, unified: true,
  };
}

export const CustomerCardModal = ({ client, phone, open, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [ordersMore, setOrdersMore] = useState([]);
  const [ordersHasMore, setOrdersHasMore] = useState(false);
  const [ordersLoadingMore, setOrdersLoadingMore] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(null);
  // البند 13/ب — شبكة المقاييس 2×2 على الجوال/iPad. مراجعة ما بعد
  // testing_agent: نقطة انكسار Modal.jsx (720) لا تغطي iPad (768) —
  // المقياس هنا مرتبط بعرض محتوى المودال (الأعمدة الأربعة) لا بعرض
  // الشاشة الكامل، فنستخدم 900 ليشمل iPad (768) والجوال معاً.
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false);

  useEffect(() => {
    const onResize = () => setIsNarrow(window.innerWidth < 900);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const load = useCallback(async () => {
    if (!phone) return;
    setLoading(true);
    setErr("");
    try {
      const path = `/api/customers/${encodeURIComponent(phone)}/profile`;
      const r = client ? await client.getAbs(path) : await api.get(`/customers/${encodeURIComponent(phone)}/profile`);
      const norm = normalizeCardData(r.data);
      setData(norm);
      setOrdersMore([]);
      setOrdersHasMore(norm.orders_has_more);
    } catch (e) {
      setErr(translateServerError(e?.response?.data?.detail, "تعذّر تحميل ملف العميل"));
    }
    setLoading(false);
  }, [client, phone]);

  const loadMoreOrders = useCallback(async () => {
    if (!data?.phone) return;
    setOrdersLoadingMore(true);
    try {
      const skip = (data.orders?.length || 0) + ordersMore.length;
      const path = `/api/customers/${encodeURIComponent(data.phone)}/orders?skip=${skip}&limit=20`;
      const r = client ? await client.getAbs(path)
        : await api.get(`/customers/${encodeURIComponent(data.phone)}/orders`, { params: { skip, limit: 20 } });
      setOrdersMore((prev) => [...prev, ...(r.data?.items || [])]);
      setOrdersHasMore(!!r.data?.has_more);
    } catch (e) {
      setErr(translateServerError(e?.response?.data?.detail, "تعذّر تحميل المزيد"));
    }
    setOrdersLoadingMore(false);
  }, [client, data, ordersMore.length]);

  useEffect(() => {
    if (open) load();
    else {
      setData(null); setErr("");
      setOrdersMore([]); setOrdersHasMore(false); setOpenInvoice(null);
    }
  }, [open, load]);

  return (
    <Modal open={open} onClose={onClose} title="ملف العميل" wide testId="inbox-customer-card">
      {loading && (
        <div data-testid="inbox-card-loading" style={{ textAlign: "center", color: C.textMuted, fontSize: 12.5, padding: 20 }}>
          ⏳ جارٍ تحميل الملف…
        </div>
      )}
      {!loading && err && (
        <div data-testid="inbox-card-error" style={{ textAlign: "center", color: C.dangerText, fontSize: 12.5, padding: 20 }}>{err}</div>
      )}
      {!loading && !err && data && (
        <div dir="rtl" data-testid="inbox-customer-card-body">
          <StickyHeader data={data} onContact={() => (client ? onClose?.() : openInboxForPhone(data.phone))} />
          <MetricsRow data={data} isNarrow={isNarrow} />
          <SummaryLine rhythm={data.rhythm} topItems={data.top_items} />

          <div style={{ marginTop: 14 }}>
            <OrdersAccordion data={data} ordersMore={ordersMore} ordersHasMore={ordersHasMore}
              ordersLoadingMore={ordersLoadingMore} onLoadMore={loadMoreOrders} onOpenInvoice={setOpenInvoice} />
            <FavoritesAccordion topItems={data.top_items} />
            <ChannelsAccordion channels={data.channels} />
            <CommsTimelineAccordion timeline={data.comms_timeline} sectionError={data.sectionErrors.comms_timeline} />
            <OffersSurveysInteractionsAccordion data={data} onOpenInvoice={setOpenInvoice} />
          </div>

          {openInvoice && (
            <InvoiceModal invoiceId={openInvoice} client={client}
              fetchUrl={`/customers/${encodeURIComponent(data.phone)}/invoice/${encodeURIComponent(openInvoice)}`}
              onClose={() => setOpenInvoice(null)} />
          )}
        </div>
      )}
    </Modal>
  );
};
